import {
  Box,
  LinearProgress,
  Stack,
  Typography,
  linearProgressClasses,
  styled,
} from "@mui/material";
import { useTranslations } from "next-intl";
import { ReactNode } from "react";

interface StatProgressProps {
  title: ReactNode;
  value: number;
  total: number;
  unit?: string;
  footer?: string;
  icon?: ReactNode;
}

const BorderLinearProgress = styled(LinearProgress, {
  shouldForwardProp: (props) => props !== "isEmpty",
})<{ isEmpty: boolean }>(({ theme, isEmpty }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === "light" ? 300 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: isEmpty
      ? theme.palette.grey[theme.palette.mode === "light" ? 300 : 800]
      : theme.palette.primary.main,
  },
}));

export const StatProgress = ({
  title,
  value = 0,
  total = 0,
  unit,
  footer,
  icon,
}: StatProgressProps) => {
  const t = useTranslations();
  return (
    <Box display="flex" width="100%" gap="0.5rem">
      {icon}
      <Stack spacing={1} flex="1">
        <Box display="flex" justifyContent="space-between">
          <Typography fontWeight="500">
            {typeof title === "string" ? t(title) : title}
          </Typography>
          <Box display="flex">
            <Typography fontWeight="500" sx={{ mr: 1 }}>
              {unit}
              {value.toLocaleString()}
            </Typography>
            /
            <Typography fontWeight="500" color="grey.100" sx={{ ml: 1 }}>
              {unit}
              {total.toLocaleString()}
            </Typography>
          </Box>
        </Box>
        <BorderLinearProgress
          isEmpty={value === 0 && total === 0}
          sx={{ height: "0.5rem", borderRadius: "0.3rem" }}
          variant="determinate"
          value={value ? (value / total) * 100 : 0}
        />
        <Typography fontWeight="500" color="grey.100" textAlign="right">
          {footer ? t(footer) : "-"}
        </Typography>
      </Stack>
    </Box>
  );
};
